import { MarkerType, type Viewport } from '@xyflow/react'
import type { AppEdge, AppNode, EdgePathType } from '../types'
import { sortParentsFirst } from '../store/flowStore'
import { sanitizeFlowGraph } from '../../../types/qualityFlowchart'
import { validateFlowGraphFromAi } from '../../../types/flowchartAi'

export type FlowJsonDocument = {
  nodes: AppNode[]
  edges: AppEdge[]
  viewport: Viewport | null
  edgeType: EdgePathType
  name: string
}

/** Serializa o grafo no formato JSON do projeto (mesmo shape do flow_json). */
export function exportToJson(doc: {
  nodes: AppNode[]
  edges: AppEdge[]
  viewport: Viewport | null
  edgeType?: EdgePathType
  name: string
}): string {
  const graph = sanitizeFlowGraph({
    nodes: doc.nodes,
    edges: doc.edges,
    viewport: doc.viewport,
    edgeType: doc.edgeType,
  })
  return JSON.stringify({ name: doc.name, ...graph }, null, 2)
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/** Retorna o objeto se tem arrays nodes e edges; senão null. */
function asGraphRecord(value: unknown): Record<string, unknown> | null {
  if (
    isRecord(value) &&
    Array.isArray(value.nodes) &&
    Array.isArray(value.edges)
  ) {
    return value
  }
  return null
}

/**
 * Extrai o grafo de formatos aceitos na importação:
 * 1. Grafo na raiz: { name?, nodes, edges, viewport?, edgeType? }
 * 2. Resposta Tess/agente: { reply?, name?, graph: { nodes, edges, ... } }
 * 3. Persistência banco: { title?, flow_json: { nodes, edges, ... } }
 */
function extractGraphPayload(obj: Record<string, unknown>): {
  graph: Record<string, unknown>
  nameHint?: string
} {
  const fromGraph = asGraphRecord(obj.graph)
  if (fromGraph) {
    return {
      graph: fromGraph,
      nameHint: firstString(
        obj.name,
        obj.title,
        fromGraph.name,
        fromGraph.title,
      ),
    }
  }
  const fromFlowJson = asGraphRecord(obj.flow_json)
  if (fromFlowJson) {
    return {
      graph: fromFlowJson,
      nameHint: firstString(
        obj.name,
        obj.title,
        fromFlowJson.name,
        fromFlowJson.title,
      ),
    }
  }
  const fromRoot = asGraphRecord(obj)
  if (fromRoot) {
    return {
      graph: fromRoot,
      nameHint: firstString(obj.name, obj.title),
    }
  }
  return {
    graph: obj,
    nameHint: firstString(obj.name, obj.title),
  }
}

/**
 * Faz o parse e a validação de um JSON de fluxograma.
 * Aceita grafo na raiz, wrapper { graph } (agente/Tess) ou { flow_json } (banco).
 * Lança Error com mensagem amigável.
 */
export function importFromJson(text: string): FlowJsonDocument {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new Error('O arquivo não contém um JSON válido.')
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('JSON inválido: esperado um objeto com "nodes" e "edges".')
  }

  const obj = parsed as Record<string, unknown>
  const { graph: rawGraph, nameHint } = extractGraphPayload(obj)

  if (!Array.isArray(rawGraph.nodes) || !Array.isArray(rawGraph.edges)) {
    throw new Error(
      'JSON inválido: não foi encontrado um grafo com "nodes" e "edges" (raiz, "graph" ou "flow_json").'
    )
  }

  const validated = validateFlowGraphFromAi(rawGraph)
  if (!validated) {
    throw new Error(
      'JSON inválido: estrutura de fluxograma não reconhecida. Verifique nodes, edges, ids únicos, tipos de nó e positions.'
    )
  }

  const edges: AppEdge[] = validated.edges.map((edge) => ({
    ...edge,
    markerEnd: edge.markerEnd ?? { type: MarkerType.ArrowClosed },
  }))

  return {
    nodes: sortParentsFirst(validated.nodes),
    edges,
    viewport: validated.viewport,
    edgeType: validated.edgeType ?? 'smoothstep',
    name: nameHint ?? 'Fluxograma importado',
  }
}
